import { useCallback, useEffect, useMemo, useState } from "react";import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import { FileDown, Plus } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import DataTable from "@/components/DataTable";

export default function Prescriptions() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [items, setItems] = useState([]);
  const [patients, setPatients] = useState([]);
  const [editing, setEditing] = useState(null);
  const [createOpen, setCreateOpen] = useState(false);

  const load = useCallback(async () => {
    try {
      const [r, p] = await Promise.all([api.get("/prescriptions"), api.get("/patients")]);
      setItems(r.data); setPatients(p.data);
    } catch (err) {
      console.error("Failed to load prescriptions:", err);
      toast.error("Failed to load");
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const bulkDelete = async (ids) => {
    try {
      const { data } = await api.post("/prescriptions/bulk-delete", { ids });
      toast.success(`Deleted ${data.deleted} prescription(s)`);
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Bulk delete failed"); }
  };

  const openPdf = useCallback(async (id) => {
    try {
      const r = await api.get(`/prescriptions/${id}/pdf`, { responseType: "blob" });
      window.open(URL.createObjectURL(r.data), "_blank");
    } catch { toast.error("Failed to load PDF"); }
  }, []);

  const renderRowExtras = useCallback(
    (rx) => (
      <Button variant="outline" size="sm" className="rounded-full text-xs h-8" onClick={() => openPdf(rx.id)} data-testid={`rx-pdf-${rx.id}`}>
        <FileDown className="w-3.5 h-3.5 mr-1" /> PDF
      </Button>
    ),
    [openPdf]
  );

  const columns = useMemo(() => ([
    {
      key: "created_at", label: "Date", sortable: true,
      accessor: (r) => r.created_at,
      render: (r) => <span className="text-[color:var(--muted)]">{new Date(r.created_at).toLocaleDateString()}</span>,
    },
    { key: "patient_name", label: "Patient", sortable: true, accessor: (r) => r.patient_name, render: (r) => <span className="font-medium text-[color:var(--ink)]">{r.patient_name}</span> },
    { key: "remedy", label: "Remedy", sortable: true, accessor: (r) => r.remedy, render: (r) => <span>{r.remedy} <span className="text-[color:var(--primary)]">{r.potency}</span></span> },
    { key: "dosage", label: "Dosage", accessor: (r) => r.dosage, render: (r) => <span className="text-[color:var(--muted)]">{r.dosage}</span> },
    { key: "follow_up_date", label: "Follow-up", sortable: true, defaultHidden: true, accessor: (r) => r.follow_up_date || "", render: (r) => r.follow_up_date || "—" },
    { key: "doctor_name", label: "Doctor", sortable: true, defaultHidden: true, accessor: (r) => r.doctor_name || "" },
  ]), []);

  return (
    <div data-testid="prescriptions-page">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-7">
        <div>
          <p className="text-xs font-semibold tracking-[0.2em] uppercase text-[color:var(--muted)]">Clinical</p>
          <h1 className="font-heading text-3xl md:text-4xl text-[color:var(--ink)] font-medium mt-1">Prescriptions</h1>
        </div>
        <RxDialog open={createOpen} setOpen={setCreateOpen} patients={patients} onSaved={load} />
      </div>

      <DataTable
        storageKey="prescriptions"
        rows={items}
        columns={columns}
        isAdmin={isAdmin}
        onEdit={isAdmin ? setEditing : undefined}
        onDeleteMany={isAdmin ? bulkDelete : undefined}
        emptyMessage="No prescriptions yet."
        minWidth={720}
        extraRowActions={renderRowExtras}
      />

      {editing && (
        <RxDialog
          open={!!editing}
          setOpen={(v) => { if (!v) setEditing(null); }}
          patients={patients}
          existing={editing}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}
    </div>
  );
}

function RxDialog({ open, setOpen, patients, onSaved, existing }) {
  const isEdit = !!existing;
  const empty = { patient_id: "", remedy: "", potency: "30C", dosage: "3 pellets, twice daily", instructions: "", follow_up_date: "", notes: "" };
  const [form, setForm] = useState(empty);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (existing) setForm({ ...empty, ...existing });
    else setForm(empty);
  }, [existing, open]);

  const save = async () => {
    setLoading(true);
    try {
      if (isEdit) {
        await api.put(`/prescriptions/${existing.id}`, form);
        toast.success("Prescription updated");
      } else {
        await api.post("/prescriptions", form);
        toast.success("Prescription saved");
      }
      setOpen(false);
      onSaved();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      {!isEdit && (
        <DialogTrigger asChild>
          <Button className="rounded-full bg-[color:var(--primary)] hover:bg-[color:var(--primary-hover)] text-white" data-testid="new-rx-btn">
            <Plus className="w-4 h-4 mr-1.5" /> New prescription
          </Button>
        </DialogTrigger>
      )}
      <DialogContent className="max-w-xl max-h-[85vh] overflow-y-auto" data-testid="rx-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">{isEdit ? "Edit prescription" : "New prescription"}</DialogTitle>
          <DialogDescription className="text-[color:var(--muted)]">{isEdit ? "Update any field, including remedy and follow-up date." : "Issue a new homoeopathic prescription."}</DialogDescription>
        </DialogHeader>
        <div className="space-y-4 mt-2">
          <div>
            <label className="text-xs font-semibold tracking-wider uppercase text-[color:var(--muted)]">Patient *</label>
            <Select value={form.patient_id} onValueChange={(v) => setForm({ ...form, patient_id: v })}>
              <SelectTrigger className="mt-1.5" data-testid="rx-patient-select"><SelectValue placeholder="Select patient" /></SelectTrigger>
              <SelectContent>
                {patients.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="grid md:grid-cols-2 gap-4">
            <F label="Remedy *" value={form.remedy} onChange={(v) => setForm({ ...form, remedy: v })} t="rx-pg-remedy" />
            <F label="Potency *" value={form.potency} onChange={(v) => setForm({ ...form, potency: v })} t="rx-pg-potency" />
            <F label="Dosage *" value={form.dosage} onChange={(v) => setForm({ ...form, dosage: v })} t="rx-pg-dosage" />
            <F label="Follow-up date" type="date" value={form.follow_up_date} onChange={(v) => setForm({ ...form, follow_up_date: v })} t="rx-pg-followup" />
          </div>
          <div>
            <label className="text-xs font-semibold tracking-wider uppercase text-[color:var(--muted)]">Instructions</label>
            <Textarea rows={2} value={form.instructions || ""} onChange={(e) => setForm({ ...form, instructions: e.target.value })} className="mt-1.5" data-testid="rx-pg-instructions" />
          </div>
          <div>
            <label className="text-xs font-semibold tracking-wider uppercase text-[color:var(--muted)]">Notes</label>
            <Textarea rows={2} value={form.notes || ""} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="mt-1.5" data-testid="rx-pg-notes" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)} data-testid="rx-pg-cancel">Cancel</Button>
          <Button onClick={save} disabled={loading || !form.patient_id || !form.remedy} className="bg-[color:var(--primary)] hover:bg-[color:var(--primary-hover)] text-white" data-testid="rx-pg-save">{loading ? "Saving…" : isEdit ? "Save changes" : "Save"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function F({ label, value, onChange, type = "text", t }) {
  return (
    <div>
      <label className="text-xs font-semibold tracking-wider uppercase text-[color:var(--muted)]">{label}</label>
      <Input type={type} value={value || ""} onChange={(e) => onChange(e.target.value)} className="mt-1.5" data-testid={t} />
    </div>
  );
}
